import { mergeRegister } from '@lexical/utils';
import { getTranslation } from '@nexelem/utils';
import {
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  LexicalEditor,
  REDO_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IS_APPLE } from '../../shared/environment';
import { IconButton } from '../IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faRotateRight } from '@fortawesome/pro-light-svg-icons';

type UndoRedoSectionProps = {
  readonly editor: LexicalEditor;
  readonly disabled?: boolean;
};

export const UndoRedoSection = ({ editor, disabled }: UndoRedoSectionProps) => {
  const { t: translate } = useTranslation();
  const t = useMemo(() => getTranslation(translate, 'components.richTextEditor.undoRedoSection'), [translate]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand<boolean>(
          CAN_UNDO_COMMAND,
          (payload) => {
            setCanUndo(payload);
            return false;
          },
          COMMAND_PRIORITY_CRITICAL,
        ),

        editor.registerCommand<boolean>(
          CAN_REDO_COMMAND,
          (payload) => {
            setCanRedo(payload);
            return false;
          },
          COMMAND_PRIORITY_CRITICAL,
        ),
      ),
    [editor],
  );

  const onClickUndo = useCallback(() => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  }, [editor]);

  const onClickRedo = useCallback(() => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  }, [editor]);

  return (
    <>
      <IconButton
        ariaLabel={t('undo.ariaLabel')}
        className="mr-1"
        disabled={!canUndo || disabled}
        StartIcon={<FontAwesomeIcon icon={faRotateLeft} />}
        title={t('undo.label', { shortcut: IS_APPLE ? '⌘Z' : 'Ctrl+Z' })}
        variant="text"
        onClick={onClickUndo}
      />
      <IconButton
        ariaLabel={t('redo.ariaLabel')}
        disabled={!canRedo || disabled}
        StartIcon={<FontAwesomeIcon icon={faRotateRight} />}
        title={t('redo.label', { shortcut: IS_APPLE ? '⌘Z' : 'Ctrl+Z' })}
        variant="text"
        onClick={onClickRedo}
      />
    </>
  );
};
UndoRedoSection.displayName = 'UndoRedoSection';
